import { createRootRoute, Outlet, Link } from "@tanstack/react-router";
import { Home, Search, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

function NotFound() {
  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #f8f6ff 0%, #ffffff 40%, #eef2ff 100%)",
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-20 -left-20 w-64 h-64 rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(99, 102, 241, 0.15), rgba(129, 140, 248, 0.1))",
          }}
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(165, 180, 252, 0.15), rgba(199, 210, 254, 0.1))",
          }}
        />
        <motion.div
          animate={{
            scale: [1.1, 1.4, 1.1],
            rotate: [0, -180, -360],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute top-1/2 left-1/3 w-80 h-80 rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(129, 140, 248, 0.1), rgba(165, 180, 252, 0.05))",
          }}
        />
      </div>

      <div className="relative flex flex-col items-center justify-center min-h-screen px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mt-8 lg:mt-0"
        >
          {/* Logo with enhanced floating animation */}
          <motion.div
            animate={{
              y: [0, -15, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="mb-12"
          >
            <img
              src="Logo_1.png"
              alt="Nightingale"
              className="mx-auto h-32 w-32"
              style={{
                objectFit: "contain",
                filter: "drop-shadow(0 10px 20px rgba(109, 74, 255, 0.25))",
              }}
            />
          </motion.div>

          {/* 404 with gradient text and glow effect */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative mb-8"
          >
            <h1
              className="text-8xl md:text-9xl font-bold relative"
              style={{
                background: "linear-gradient(135deg, #6D4AFF 0%, #4338ca 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textShadow: "0 0 60px rgba(109, 74, 255, 0.3)",
              }}
            >
              404
            </h1>
            <motion.div
              animate={{
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute inset-0 blur-3xl pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(109, 74, 255, 0.4), transparent)",
                zIndex: -1,
              }}
            />
          </motion.div>

          {/* Error message with better typography */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Hoppla! Seite nicht gefunden
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-md mx-auto leading-relaxed">
              Die gesuchte Seite scheint sich versteckt zu haben. Keine Sorge,
              wir helfen Ihnen gerne weiter!
            </p>
          </motion.div>

          {/* Action buttons with better styling */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/"
              className="group relative inline-flex items-center gap-3 px-8 py-4 text-white rounded-2xl font-semibold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #6D4AFF 0%, #4f46e5 100%)",
              }}
            >
              <span
                className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300"
                style={{ background: "rgba(255, 255, 255, 0.2)" }}
              />
              <Home className="h-5 w-5 relative z-10" />
              <span className="relative z-10 whitespace-nowrap">
                Zur Startseite
              </span>
            </Link>

            <button
              onClick={() => window.history.back()}
              className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold text-lg transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105"
              style={{
                border: "2px solid #e5e7eb",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8f6ff";
                e.currentTarget.style.borderColor = "#6D4AFF";
                e.currentTarget.style.color = "#6D4AFF";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.color = "#374151";
              }}
            >
              <ArrowLeft className="h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1" />
              <span className="whitespace-nowrap">Zurück</span>
            </button>
          </motion.div>

          {/* Search suggestion with animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16 p-6 rounded-2xl shadow-lg"
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(109, 74, 255, 0.1)",
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <Search className="h-5 w-5" style={{ color: "#6D4AFF" }} />
              <p className="text-sm font-medium text-gray-700">
                Vielleicht finden Sie hier, was Sie suchen:
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  background: "#f8f6ff",
                  color: "#6D4AFF",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#6D4AFF";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f8f6ff";
                  e.currentTarget.style.color = "#6D4AFF";
                }}
              >
                Chat starten
              </Link>
              <Link
                to="/privacy"
                className="px-4 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Datenschutz
              </Link>
              <Link
                to="/terms"
                className="px-4 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Nutzungsbedingungen
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
});
